import { useState } from 'react';
import { useStore } from '../context/ProductStore';
import { useAuth } from '../context/AuthContext';
import { Store, Lock, Bell, Palette, Upload, X, Image as ImageIcon, Trash2, CreditCard } from 'lucide-react';
import { uploadImage } from '../utils/upload';

export default function Settings() {
  const { stats, settings, updateLogo, clearAllData, paymentGatewayEnabled, savePaymentGatewayStatus, codEnabled, saveCodStatus } = useStore();
  const { user } = useAuth();
  const [clearing, setClearing] = useState(false);

  const handleClearCache = async () => {
    if (!window.confirm('⚠️ Yeh sab data clear kar dega (products, categories, homepage images) aur default data reload hoga. Confirm karo?')) return;
    setClearing(true);
    await clearAllData(); // reloads page
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Image too large. Please keep it under 2MB.');
        return;
      }
      try {
        const url = await uploadImage(file);
        updateLogo(url);
      } catch (err) {
        console.error("Upload error:", err);
      }
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Settings</h1>
          <p className="admin-page-sub">Manage your store preferences & branding</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="admin-card" style={{ gridColumn: '1/-1' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, background: '#f5f3ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
              <Palette size={18} />
            </div>
            <div>
              <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15 }}>Store Branding</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Customize your store's visual identity</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Store Logo</p>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                This logo will appear on the header across the storefront and admin panel.
              </p>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <label className="admin-btn-primary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Upload size={16} />
                  Upload Logo
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </label>
                {settings.logo && (
                  <button 
                    onClick={() => updateLogo(null)}
                    className="admin-btn-outline" 
                    style={{ color: '#dc2626', borderColor: '#fee2e2' }}
                  >
                    <X size={16} /> Remove
                  </button>
                )}
              </div>
            </div>

            <div style={{ width: '200px' }}>
              <p style={{ fontWeight: 600, fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 10 }}>Preview</p>
              <div style={{ 
                height: '140px', 
                background: '#f9fafb', 
                borderRadius: 12, 
                border: '2px dashed #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                padding: 10
              }}>
                {settings.logo ? (
                  <img src={settings.logo} alt="Logo Preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                    <ImageIcon size={24} style={{ marginBottom: 4 }} />
                    <p style={{ fontSize: 11 }}>No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, background: '#fee2e2', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D62828' }}>
              <Store size={18} />
            </div>
            <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15 }}>Store Info</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[['Store Name', 'KOA'], ['Currency', '₹ INR'], ['Products', stats.total], ['Categories', stats.categories]].map(([k,v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}>
                <span style={{ color: '#6b7280' }}>{k}</span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, background: '#dbeafe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8' }}>
              <Lock size={18} />
            </div>
            <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15 }}>Admin Account</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[['Username', user?.username], ['Role', 'Administrator'], ['Access', 'Full Access'], ['Session', 'Active']].map(([k,v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}>
                <span style={{ color: '#6b7280' }}>{k}</span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{v}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
            To change credentials, edit <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>src/admin/context/AuthContext.jsx</code>
          </p>
        </div>

        <div className="admin-card">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, background: '#ecfdf5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
              <CreditCard size={18} />
            </div>
            <div>
              <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15 }}>Payment Methods</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Enable or disable payment options</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Razorpay Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#f9fafb', borderRadius: 10 }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Razorpay (UPI/Card)</p>
                <p style={{ fontSize: 12, color: '#6b7280' }}>Accept online payments via UPI, Cards, etc.</p>
              </div>
              <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input 
                  type="checkbox" 
                  checked={paymentGatewayEnabled} 
                  onChange={(e) => savePaymentGatewayStatus(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }} 
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: paymentGatewayEnabled ? '#10b981' : '#ccc',
                  transition: '.4s',
                  borderRadius: '24px'
                }}>
                  <span style={{
                    position: 'absolute',
                    height: '18px', width: '18px',
                    left: paymentGatewayEnabled ? '22px' : '4px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%'
                  }}></span>
                </span>
              </label>
            </div>

            {/* COD Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#f9fafb', borderRadius: 10 }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Cash on Delivery (COD)</p>
                <p style={{ fontSize: 12, color: '#6b7280' }}>Allow customers to pay at the time of delivery.</p>
              </div>
              <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input 
                  type="checkbox" 
                  checked={codEnabled} 
                  onChange={(e) => saveCodStatus(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }} 
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: codEnabled ? '#10b981' : '#ccc',
                  transition: '.4s',
                  borderRadius: '24px'
                }}>
                  <span style={{
                    position: 'absolute',
                    height: '18px', width: '18px',
                    left: codEnabled ? '22px' : '4px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%'
                  }}></span>
                </span>
              </label>
            </div>
          </div>
          
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
            At least one payment method should be active for customers to complete checkout.
          </p>
        </div>

        <div className="admin-card" style={{ gridColumn: '1/-1' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, background: '#fef3c7', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#92400e' }}>
              <Bell size={18} />
            </div>
            <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15 }}>Quick Help</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              ['🔑 Login', 'Username: admin\nPassword: KOA@2024'],
              ['📦 Products', 'All products saved in IndexedDB. Changes persist across sessions.'],
              ['🖼️ Images', 'Upload files via admin — they go directly to Cloudinary.'],
              ['🔄 Reset', 'Use the Clear Cache button below to restore all default data.'],
              ['🌐 SEO', 'Fill Meta Title, Description & Slug for each product.'],
              ['🏷️ Coupons', 'Try KOA10 on the storefront for 10% off.'],
            ].map(([title, desc]) => (
              <div key={title} style={{ background: '#f9fafb', borderRadius: 12, padding: '16px' }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 6 }}>{title}</p>
                <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="admin-card" style={{ gridColumn: '1/-1', border: '1.5px solid #fee2e2' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ width: 40, height: 40, background: '#fee2e2', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
              <Trash2 size={18} />
            </div>
            <div>
              <h3 style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 15, color: '#dc2626' }}>Danger Zone</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Ye actions reversible nahi hain</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', borderRadius: 12, padding: '16px 20px' }}>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 4 }}>🗄️ Clear Image Cache & Reset Data</p>
              <p style={{ fontSize: 13, color: '#6b7280' }}>IndexedDB wipe karega aur page reload hoga. Purani cached images hat jayengi, Cloudinary uploads fresh milenge.</p>
            </div>
            <button
              onClick={handleClearCache}
              disabled={clearing}
              style={{
                background: clearing ? '#9ca3af' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: clearing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginLeft: 20,
              }}
            >
              <Trash2 size={15} />
              {clearing ? 'Clearing...' : 'Clear Cache'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
